import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Link } from "@/components/site/LocaleLink";
import { PageHero } from "@/components/site/PageHero";
import { ProductCard } from "@/components/site/ProductCard";
import { RevealGroup, RevealItem } from "@/components/site/Reveal";
import { useLang, useL } from "@/context/LanguageContext";
import { useSeo } from "@/hooks/useSeo";
import { PRODUCTS, CATEGORIES, SPECIES } from "@/data/products";
import { PARTNERS } from "@/data/copy";
import { IMG } from "@/data/images";

export default function Products() {
  const { t, isRtl } = useLang();
  const L = useL();
  const [params, setParams] = useSearchParams();
  const [category, setCategory] = useState(params.get("category") || "all");
  const [species, setSpecies] = useState("all");
  const [query, setQuery] = useState("");

  // The partner tiles on the home page link here with ?manufacturer=<name>,
  // which turns this page into that manufacturer's catalogue: its name becomes
  // the page title and only its products are listed.
  const manufacturer = params.get("manufacturer") || "";
  const partner = PARTNERS.find((p) => p.manufacturer === manufacturer);

  useSeo({
    title: partner
      ? `${partner.name} | ${isRtl ? "شركة الأبرد" : "Al-Abrad"}`
      : isRtl
        ? "المنتجات | شركة الأبرد"
        : "Products | Al-Abrad",
    description: partner ? t.products.byPartner : t.products.sub,
  });

  useEffect(() => {
    setCategory(params.get("category") || "all");
  }, [params]);

  const setCat = (id) => {
    setCategory(id);
    const next = {};
    if (id !== "all") next.category = id;
    if (manufacturer) next.manufacturer = manufacturer;
    setParams(next);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PRODUCTS.filter((p) => {
      if (manufacturer && p.manufacturer !== manufacturer) return false;
      if (category !== "all" && p.category !== category) return false;
      if (species !== "all" && !p.species.includes(species)) return false;
      if (q) {
        const hay = `${p.name.en} ${p.name.ar} ${p.manufacturer}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [category, species, query, manufacturer]);

  const hasFilters = category !== "all" || species !== "all" || query || manufacturer;

  const clear = () => {
    setCategory("all");
    setSpecies("all");
    setQuery("");
    setParams({});
  };

  // The grid remounts on filter changes so the reveal animation replays.
  const gridKey = `${manufacturer}-${category}-${species}-${query}`;

  return (
    <>
      <PageHero
        title={partner ? partner.name : t.products.title}
        subtitle={partner ? t.products.byPartner : t.products.sub}
        image={IMG.barn}
        crumb={t.nav.products}
      />

      <section className="bg-cream py-16 lg:py-24">
        <div className="container-x">
          {/* controls */}
          <div className="mb-10 flex flex-col gap-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full lg:max-w-sm">
                <Search className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-stone start-4" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t.products.search}
                  data-testid="product-search"
                  className="w-full rounded-full border border-line bg-white py-3 text-sm text-ink outline-none transition-colors focus:border-forest ps-11 pe-4"
                />
              </div>
              <div className="flex items-center gap-2 text-sm text-stone">
                <SlidersHorizontal className="h-4 w-4" />
                <span data-testid="results-count">
                  {filtered.length} {t.products.results}
                </span>
                {hasFilters && (
                  <button
                    onClick={clear}
                    data-testid="clear-filters"
                    className="ms-2 inline-flex items-center gap-1 rounded-full bg-sage px-3 py-1 text-xs font-medium text-forest transition-colors hover:bg-forest hover:text-cream"
                  >
                    <X className="h-3 w-3" />
                    {t.products.clear}
                  </button>
                )}
              </div>
            </div>

            {manufacturer && (
              <div className="flex flex-wrap items-center gap-2" data-testid="manufacturer-filter">
                <span className="text-xs font-semibold uppercase tracking-wider text-stone">
                  {t.products.madeBy}:
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-forest bg-forest px-4 py-1.5 text-xs font-medium text-cream">
                  {partner ? partner.name : manufacturer}
                </span>
                <Link
                  to="/products"
                  data-testid="clear-manufacturer"
                  className="inline-flex items-center gap-1 rounded-full border border-line bg-white px-4 py-1.5 text-xs font-medium text-ink transition-all duration-300 hover:border-forest/40 hover:bg-sage"
                >
                  <X className="h-3 w-3" />
                  {t.products.allProducts}
                </Link>
              </div>
            )}

            {/* category pills */}
            <div className="flex flex-wrap gap-2">
              <FilterPill active={category === "all"} onClick={() => setCat("all")} testid="cat-all">
                {t.products.allCategories}
              </FilterPill>
              {CATEGORIES.map((c) => (
                <FilterPill
                  key={c.id}
                  active={category === c.id}
                  onClick={() => setCat(c.id)}
                  testid={`cat-${c.id}`}
                >
                  {L(c.name)}
                </FilterPill>
              ))}
            </div>

            {/* species pills */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="me-1 text-xs font-semibold uppercase tracking-wider text-stone">
                {t.products.filterSpecies}:
              </span>
              <FilterPill small active={species === "all"} onClick={() => setSpecies("all")} testid="sp-all">
                {t.products.allSpecies}
              </FilterPill>
              {SPECIES.map((s) => (
                <FilterPill
                  key={s.id}
                  small
                  active={species === s.id}
                  onClick={() => setSpecies(s.id)}
                  testid={`sp-${s.id}`}
                >
                  {L(s.name)}
                </FilterPill>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-line bg-white py-24 text-center" data-testid="empty-state">
              <p className="text-lg text-stone">{t.products.empty}</p>
              <button
                onClick={clear}
                className="mt-4 rounded-full bg-forest px-6 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-forest-dark"
              >
                {t.products.clear}
              </button>
            </div>
          ) : (
            <RevealGroup className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" key={gridKey}>
              {filtered.map((p) => (
                <RevealItem key={p.slug}>
                  <ProductCard product={p} />
                </RevealItem>
              ))}
            </RevealGroup>
          )}
        </div>
      </section>
    </>
  );
}

const FilterPill = ({ children, active, onClick, small, testid }) => (
  <button
    onClick={onClick}
    data-testid={`filter-${testid}`}
    className={`rounded-full border font-medium transition-all duration-300 ${
      small ? "px-4 py-1.5 text-xs" : "px-5 py-2 text-sm"
    } ${
      active
        ? "border-forest bg-forest text-cream"
        : "border-line bg-white text-ink hover:border-forest/40 hover:bg-sage"
    }`}
  >
    {children}
  </button>
);
